/**
 * HTTP client with automatic token refresh on 401.
 * Access token stored in-memory (never in localStorage/cookie).
 */

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include', // sends httpOnly cookie
    });

    if (!res.ok) {
      accessToken = null;
      return null;
    }

    const data = await res.json();
    accessToken = data.accessToken;
    return accessToken;
  } catch {
    accessToken = null;
    return null;
  }
}

/** Ensure only one refresh request is in-flight at a time */
export function doRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Fetch wrapper that attaches Bearer token and auto-retries on 401.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  let res = await fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  });

  // If 401, try to refresh and retry once
  if (res.status === 401) {
    const newToken = await doRefresh();
    if (newToken) {
      headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(input, {
        ...init,
        headers,
        credentials: 'include',
      });
    }
  }

  return res;
}
